import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Navbar from '../components/Navbar';
import { getPost } from '../blog';

const SITE_URL = 'https://www.workbees.space';

/** 动态更新 meta 标签（SEO + 分享卡片） */
function updateMetaTag(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    if (name.startsWith('og:')) {
      meta.setAttribute('property', name);
    } else {
      meta.setAttribute('name', name);
    }
    document.head.appendChild(meta);
  }
  meta.content = content;
}

// 与产品文档页一致的 Markdown 渲染风格
const markdownComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-3xl font-bold mt-8 mb-6 text-slate-800">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-2xl font-bold mt-10 mb-4 text-slate-800 flex items-center gap-2">
      <span className="w-1 h-6 bg-indigo-500 rounded-full"></span>
      {children}
    </h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-xl font-semibold mt-6 mb-3 text-slate-800">{children}</h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-4 text-slate-600 leading-relaxed">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc list-inside my-4 space-y-2 text-slate-700">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal list-inside my-4 space-y-2 text-slate-700">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="text-slate-600">{children}</li>
  ),
  code: ({ inline, children, ...props }: { inline?: boolean; children?: React.ReactNode }) => {
    if (inline) {
      return (
        <code className="px-1.5 py-0.5 mx-0.5 rounded bg-slate-100 text-indigo-600 font-mono text-sm border border-indigo-100" {...props}>
          {children}
        </code>
      );
    }
    return (
      <pre className="block px-4 py-3 my-4 rounded-lg bg-slate-900 text-slate-100 font-mono text-sm overflow-auto border border-slate-700">
        <code {...props}>{children}</code>
      </pre>
    );
  },
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l-4 border-indigo-300 bg-indigo-50 pl-4 pr-4 py-3 my-6 italic text-slate-600 rounded-r">
      {children}
    </blockquote>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <table className="w-full my-6 border-collapse border border-slate-200 rounded-lg overflow-hidden">
      {children}
    </table>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="border-b-2 border-slate-300 px-4 py-3 text-left font-semibold text-slate-800 bg-slate-50">
      {children}
    </th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="border-b border-slate-200 px-4 py-3 text-slate-600">
      {children}
    </td>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      className="text-indigo-600 underline underline-offset-4 hover:text-indigo-700 transition-colors"
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-8 border-t-2 border-slate-200" />,
  img: ({ src, alt }: { src?: string; alt?: string }) => (
    <img src={src} alt={alt || ''} className="max-w-full rounded-xl my-6 border border-slate-100" />
  ),
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPost(slug) : undefined;

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Teegal 博客`;
      const desc = post.description || post.content.slice(0, 150).replace(/[#*`>\-\n]/g, ' ').trim();
      updateMetaTag('description', desc);
      updateMetaTag('og:title', post.title);
      updateMetaTag('og:description', desc);
      updateMetaTag('og:type', 'article');
      updateMetaTag('og:url', `${SITE_URL}/blog/${post.slug}`);
      document.querySelector('link[rel="canonical"]')?.setAttribute('href', `${SITE_URL}/blog/${post.slug}`);
    }
    window.scrollTo(0, 0);
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 pt-40 pb-24 text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">文章不存在</h1>
          <Link to="/blog" className="text-indigo-600 hover:underline">← 返回博客</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="pt-14">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-8 transition-colors">
            <ArrowLeft size={15} />
            返回博客
          </Link>

          <header className="mb-10">
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="flex items-center gap-1.5 text-sm text-slate-400">
              <Calendar size={14} />
              <span>{post.date}</span>
            </div>
          </header>

          <div className="bg-white rounded-2xl border border-slate-100 px-6 sm:px-10 py-8">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents as any}
            >
              {post.content}
            </ReactMarkdown>
          </div>
        </article>
      </main>
    </div>
  );
};

export default BlogPost;
